import { Component } from '@theme/component';

/**
 * NKUK-1044: Mobile drill-down menu for the new navigation (see `snippets/nk-mobile-menu.liquid`).
 * Only the screens on the current path are shown.
 */
const SLIDE_DURATION = 320;
const MOBILE_QUERY = '(max-width: 1149px), (hover: none)';
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled])';

class NkMobileMenu extends Component {
  #abortController = new AbortController();

  /** @type {string[]} Screens opened on top of the main screen, in order. */
  #stack = [];

  /** @type {number | undefined} */
  #hideTimer;

  #mobile = window.matchMedia(MOBILE_QUERY);

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#toggleButton?.addEventListener('click', this.toggle, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    document.addEventListener('keydown', this.#onKeyDown, { signal });
    // Leaving the mobile layout (e.g. rotating a tablet) closes the menu.
    this.#mobile.addEventListener('change', () => this.close(), { signal });
    window.addEventListener('scroll', this.#positionBelowHeader, { signal, passive: true });
    window.addEventListener('resize', this.#positionBelowHeader, { signal });

    this.inert = true;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    clearTimeout(this.#hideTimer);
    document.documentElement.classList.remove('nk-menu-open');
  }

  get #header() {
    return this.closest('nk-header');
  }

  get #toggleButton() {
    return this.#header?.querySelector('[data-nk-menu-toggle]') ?? null;
  }

  get #track() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-track]'));
  }

  get isOpen() {
    return this.hasAttribute('data-open');
  }

  toggle = () => {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  };

  open() {
    const header = this.#header;

    // Lock the page first, as it can move the header under a sticky announcement bar.
    document.documentElement.classList.add('nk-menu-open');
    this.#reset();
    header?.setAttribute('data-mobile-open', '');
    this.inert = false;
    this.setAttribute('data-open', '');
    this.#toggleButton?.setAttribute('aria-expanded', 'true');
    this.#positionBelowHeader();
  }

  /** Keeps the drawer starting at the bottom of the header bar. */
  #positionBelowHeader = () => {
    if (!this.isOpen) return;
    const bar = this.#header?.querySelector('.nk-header__bar');
    if (bar) this.style.setProperty('--nk-mobile-top', `${bar.getBoundingClientRect().bottom}px`);
  };

  close() {
    if (!this.isOpen) return;

    const hadFocus = this.contains(document.activeElement);
    this.removeAttribute('data-open');
    this.inert = true;
    this.#header?.removeAttribute('data-mobile-open');
    this.#toggleButton?.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('nk-menu-open');
    if (hadFocus) this.#toggleButton?.focus();
  }

  /** Back to the main screen with no animation, ready to open. */
  #reset() {
    clearTimeout(this.#hideTimer);
    this.#stack = [];
    for (const screen of this.#screens()) screen.hidden = screen.dataset.screen !== 'main';

    const track = this.#track;
    if (!track) return;
    track.style.transition = 'none';
    this.#slide();
    track.getBoundingClientRect();
    track.style.removeProperty('transition');
  }

  /** @param {string} id */
  #push(id) {
    const screen = this.#screen(id);
    if (!screen) return;

    clearTimeout(this.#hideTimer);
    screen.hidden = false;
    this.#stack.push(id);
    // Wait a frame so the newly shown screen is laid out before it slides in.
    requestAnimationFrame(() => {
      this.#slide();
      this.#focusFirst(screen);
    });
  }

  #pop() {
    const id = this.#stack.pop();
    if (!id) return;

    this.#slide();
    const opener = this.querySelector(`[data-go="${CSS.escape(id)}"]`);
    if (opener instanceof HTMLElement) opener.focus();

    this.#hideTimer = setTimeout(() => {
      const screen = this.#screen(id);
      if (screen && !this.#stack.includes(id)) screen.hidden = true;
    }, SLIDE_DURATION);
  }

  /** Moves the track to the current screen and keeps the off-screen ones out of the Tab order. */
  #slide() {
    const track = this.#track;
    if (track) track.style.transform = `translateX(-${this.#stack.length * 100}%)`;

    const current = this.#stack.at(-1) ?? 'main';
    for (const screen of this.#screens()) screen.inert = screen.dataset.screen !== current;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const go = /** @type {HTMLElement | null} */ (event.target.closest('[data-go]'));
    if (go?.dataset.go) {
      event.preventDefault();
      this.#push(go.dataset.go);
      return;
    }

    if (event.target.closest('[data-back]')) {
      event.preventDefault();
      this.#pop();
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key === 'Escape' && this.isOpen) this.close();
  };

  /** @param {HTMLElement} screen */
  #focusFirst(screen) {
    const first = screen.querySelector(FOCUSABLE);
    if (first instanceof HTMLElement) first.focus({ preventScroll: true });
  }

  #screens() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-screen]')]);
  }

  /** @param {string} id */
  #screen(id) {
    return /** @type {HTMLElement | null} */ (this.querySelector(`[data-screen="${CSS.escape(id)}"]`));
  }
}

if (!customElements.get('nk-mobile-menu')) {
  customElements.define('nk-mobile-menu', NkMobileMenu);
}
